import { useState, useEffect } from 'react';

interface MarketItem {
  label: string;
  value: string;
  changePct?: number;
  hint?: string;
}

const COIN_IDS = 'bitcoin,ethereum,solana,tether';

/**
 * Barra de mercado: BTC, ETH, SOL (BRL), dólar (USDT/BRL), Fear & Greed,
 * dominância do BTC e horário da última atualização.
 * Fontes: CoinGecko e alternative.me.
 */
export const PriceTicker: React.FC = () => {
  const [items, setItems] = useState<MarketItem[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [marketsRes, globalRes, fngRes] = await Promise.all([
          fetch(
            `https://api.coingecko.com/api/v3/coins/markets?vs_currency=brl&ids=${COIN_IDS}&price_change_percentage=24h`
          ).then((r) => r.json()),
          fetch('https://api.coingecko.com/api/v3/global')
            .then((r) => r.json())
            .catch(() => null),
          fetch('https://api.alternative.me/fng/?limit=1')
            .then((r) => r.json())
            .catch(() => null),
        ]);

        if (!Array.isArray(marketsRes)) return;

        const bySymbol: Record<string, any> = {};
        for (const coin of marketsRes) bySymbol[coin.id] = coin;

        const formatBrl = (value: number) =>
          value >= 1000
            ? `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
            : `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

        const next: MarketItem[] = [];
        for (const [id, label] of [
          ['bitcoin', 'BTC'],
          ['ethereum', 'ETH'],
          ['solana', 'SOL'],
        ] as const) {
          const coin = bySymbol[id];
          if (coin) {
            next.push({
              label,
              value: formatBrl(coin.current_price),
              changePct: coin.price_change_percentage_24h ?? undefined,
            });
          }
        }

        // USDT/BRL ≈ dólar
        if (bySymbol.tether) {
          next.push({
            label: 'Dólar',
            value: `R$ ${bySymbol.tether.current_price.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
            hint: 'USDT/BRL',
          });
        }

        const btcDominance = globalRes?.data?.market_cap_percentage?.btc;
        if (typeof btcDominance === 'number') {
          next.push({ label: 'Dom. BTC', value: `${btcDominance.toFixed(1)}%` });
        }

        const fng = fngRes?.data?.[0];
        if (fng?.value) {
          const fngValue = Number(fng.value);
          const fngLabel =
            fngValue <= 25 ? 'Medo extremo' : fngValue <= 45 ? 'Medo' : fngValue <= 55 ? 'Neutro' : fngValue <= 75 ? 'Ganância' : 'Ganância extrema';
          next.push({ label: 'Fear & Greed', value: `${fngValue} · ${fngLabel}` });
        }

        setItems(next);
        setUpdatedAt(new Date());
      } catch {
        // silent — a barra simplesmente não atualiza
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="bg-gray-900 text-white py-2 border-b border-gray-700">
      <div className="container mx-auto px-4 flex items-center gap-x-6 gap-y-1 overflow-x-auto whitespace-nowrap text-sm">
        {items.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1.5 flex-shrink-0">
            <span className="font-bold text-yellow-400">{item.label}</span>
            <span className="text-gray-200">{item.value}</span>
            {typeof item.changePct === 'number' && (
              <span className={item.changePct >= 0 ? 'text-green-400' : 'text-red-400'}>
                {item.changePct >= 0 ? '▲' : '▼'} {Math.abs(item.changePct).toFixed(2)}%
              </span>
            )}
            {item.hint && <span className="text-gray-500 text-xs">({item.hint})</span>}
          </span>
        ))}
        {updatedAt && (
          <span className="ml-auto flex-shrink-0 text-xs text-gray-500">
            Atualizado {updatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} ·
            CoinGecko
          </span>
        )}
      </div>
    </div>
  );
};
